import { UpsShipmentInput } from '../models/ups-shipment.model';
import { partyToUps } from './build-ups-shipment-request.function';

// Shared pieces between the single-service Rate and the multi-service Shop requests.
function buildRateShipment(input: UpsShipmentInput) {
    const shipper = partyToUps(input.shipper);
    const shipFrom = partyToUps(input.shipFrom ?? input.shipper);
    const shipTo = partyToUps(input.shipTo);

    if (input.residential) {
        (shipTo.Address as Record<string, unknown>)['ResidentialAddressIndicator'] = '';
    }

    const Package = input.packages.map((pkg) => {
        const p: Record<string, unknown> = {
            PackagingType: { Code: '02' }, // rating uses PackagingType (shipping uses Packaging)
            PackageWeight: { UnitOfMeasurement: { Code: 'LBS' }, Weight: String(pkg.weightLbs) },
        };
        if (pkg.lengthIn && pkg.widthIn && pkg.heightIn) {
            p['Dimensions'] = {
                UnitOfMeasurement: { Code: 'IN' },
                Length: String(pkg.lengthIn),
                Width: String(pkg.widthIn),
                Height: String(pkg.heightIn),
            };
        }
        // Declared (insured) value — UPS prices the insurance surcharge into the rate.
        const declared = Number(pkg.declaredValue);
        if (declared > 0) {
            p['PackageServiceOptions'] = { DeclaredValue: { CurrencyCode: 'USD', MonetaryValue: String(declared) } };
        }
        return p;
    });

    return {
        Shipper: shipper,
        ShipFrom: shipFrom,
        ShipTo: shipTo,
        Package,
        ShipmentRatingOptions: {
            // Account's negotiated rate in addition to the published rate.
            NegotiatedRatesIndicator: 'Y',
            // Return the per-surcharge itemized breakdown (fuel, residential, DAS, …).
            RateChartIndicator: 'Y',
        },
    };
}

/**
 * Map a UpsShipmentInput into a UPS RateRequest for a single service (no label). The
 * Rating API uses `PackagingType` (not Packaging); negotiated rates via
 * ShipmentRatingOptions.NegotiatedRatesIndicator.
 */
export function buildUpsRateRequest(input: UpsShipmentInput) {
    return {
        RateRequest: {
            // SubVersion 1707+ is required for UPS to return the ItemizedCharges breakdown.
            Request: { RequestOption: 'Rate', SubVersion: '1707' },
            Shipment: {
                ...buildRateShipment(input),
                Service: { Code: input.serviceCode, Description: input.serviceDescription || '' },
            },
        },
    };
}

/**
 * "Shop" rate request: returns rates for ALL available services in one call, with
 * time-in-transit (estimated delivery dates). No Service is specified. Used to populate
 * the service comparison cards without making one call per service.
 */
export function buildUpsShopRateRequest(input: UpsShipmentInput) {
    return {
        RateRequest: {
            Request: { RequestOption: 'Shoptimeintransit', SubVersion: '1707' },
            Shipment: {
                ...buildRateShipment(input),
                // Required for time-in-transit; 03 = non-document (parcel).
                DeliveryTimeInformation: { PackageBillType: '03' },
            },
        },
    };
}
